import React, { useEffect, useState } from "react";
import { SearchIcon } from "lucide-react";
import { Checkbox } from "@component/checkbox";
import ApplicantItem from "@component/applicant-item";
import LoadingBox from "@component/loading-box";
import { EmptyState } from "@component/empty-state";
import { Applicant, ApplicantJobInsight } from "@type/applicant";
import { Job } from "@type/job";
import { getJobApplicantInsight } from "@service/job";

interface TableListApplicantProps {
    isLoading: boolean;
    applicants: Applicant[];
    job: Job | null;
    activeFunnelStage: string;
    isAllSelected: boolean;
    sortDirection: string;
    shortlistedCountMap: Record<string, number>;
    commentCountMap: Record<string, number>;
    onSelectAllApplicants: (checked: boolean) => void;
    onSortDirection: (field: string, direction: "asc" | "desc" | "") => void;
    onSortBy: (field: string) => React.ReactNode;
    onViewProfile: (applicant: Applicant) => void;
    onViewReview?: (applicant: Applicant) => void;
    onShowRejectReason: (applicant: Applicant) => void;
    onUpdateScoreSimilarity: (applicantID: string, score: number) => void;
    onSelectedApplicant: (applicantID: string, isSelected: boolean) => void;
    onApplicantStatusChange: (applicantID: string, status: string) => void;
    onDeleteCandidate?: (applicant: Applicant) => void;
    // Hiring-flow board columns (#3073/#3075) — only for custom-flow jobs.
    showFlowColumns?: boolean;
    onFlowAction?: () => void;
    // #3186: reject a gate-flagged candidate from the Flagged tab.
    onRejectFlagged?: (applicant: Applicant) => void;
}

export default function TableListApplicant({
    isLoading,
    applicants,
    job,
    activeFunnelStage,
    isAllSelected,
    sortDirection,
    shortlistedCountMap,
    commentCountMap,
    onSelectAllApplicants,
    onSortDirection,
    onSortBy,
    onViewProfile,
    onViewReview,
    onShowRejectReason,
    onUpdateScoreSimilarity,
    onSelectedApplicant,
    onApplicantStatusChange,
    onDeleteCandidate,
    showFlowColumns = false,
    onFlowAction,
    onRejectFlagged,
}: TableListApplicantProps) {
    // The Flagged tab (#3186) is the pending_assessment funnel filtered to the two
    // gate-hold statuses, so it renders the SAME columns as Screening In Progress.
    const stageForColumns = activeFunnelStage === "flagged" ? "pending_assessment" : activeFunnelStage;
    const [insightMap, setInsightMap] = useState<Record<string, ApplicantJobInsight | undefined>>({});
    const [loadingInsightIds, setLoadingInsightIds] = useState<Set<string>>(new Set());

    const applicantIds = applicants.map(a => a.applicant_id).join(",");

    useEffect(() => {
        if (!job?.id || applicants.length === 0) return;

        const jobId = job.id;
        const toFetch: Applicant[] = [];

        for (const applicant of applicants) {
            const cached = sessionStorage.getItem(`applicant:insight:${jobId}:${applicant.id}`);
            if (cached) {
                setInsightMap(prev => ({ ...prev, [applicant.applicant_id]: JSON.parse(cached) }));
            } else {
                toFetch.push(applicant);
            }
        }

        if (toFetch.length === 0) return;

        setLoadingInsightIds(new Set(toFetch.map(a => a.applicant_id)));

        Promise.allSettled(
            toFetch.map(applicant =>
                getJobApplicantInsight(jobId, applicant.applicant_id).then(resp => ({
                    applicant_id: applicant.applicant_id,
                    item_id: applicant.id,
                    data: resp.status === 200 ? resp.data.data : undefined,
                }))
            )
        ).then(results => {
            const updates: Record<string, ApplicantJobInsight | undefined> = {};
            for (const result of results) {
                if (result.status === "fulfilled") {
                    updates[result.value.applicant_id] = result.value.data;
                    if (result.value.data) {
                        sessionStorage.setItem(
                            `applicant:insight:${jobId}:${result.value.item_id}`,
                            JSON.stringify(result.value.data)
                        );
                    }
                }
            }
            setInsightMap(prev => ({ ...prev, ...updates }));
            setLoadingInsightIds(new Set());
        });
    }, [job?.id, applicantIds]);
    const getEmptyStateContent = (stage: string) => {
        const content: Record<string, { title: string; description: React.ReactNode }> = {
            applied: {
                title: "No candidates yet.",
                description: (
                    <>Candidates will appear here once they submit their CV for this role.</>
                ),
            },
            pending_assessment: {
                title: "No candidates yet.",
                description: (
                    <>
                        Candidates will appear here after you send them a screening invitation.
                    </>
                ),
            },
            flagged: {
                title: "No flagged candidates.",
                description: (
                    <>
                        Candidates paused by a hiring-flow gate (knockout answer or missed deadline) will appear here for you to review.
                    </>
                ),
            },
            to_review: {
                title: "No candidates yet.",
                description: (
                    <>
                        Candidates will appear here once they complete their screening.
                    </>
                ),
            },
            offers: {
                title: "No candidates yet.",
                description: (
                    <>
                        Candidates will appear here once an offer is sent.
                    </>
                ),
            },
            closed: {
                title: "No candidates yet.",
                description: (
                    <>
                        Candidates will appear here once their applications are
                        <br />
                        closed or finalized.
                    </>
                ),
            },
        };

        return content[stage] || {
            title: "No applicants found.",
            description: (
                <>
                    We couldn't find any applicants matching your search criteria. Try adjusting your filters or search terms.
                </>
            ),
        };
    };

    // Column count for the empty / loading rows — must match the header, which
    // varies by funnel stage and whether the job runs a custom hiring flow
    // (showFlowColumns adds Flow Progress + Current Step on Screening, Next Step
    // on Review). Keep this in sync with the <th> list below.
    const columnCount =
        9 + // checkbox + candidate + location + applied + status + match + insights + 2 trailing action cells
        (stageForColumns === "pending_assessment" ? 1 : 0) + // Sent Date
        (stageForColumns === "to_review" ? 1 : 0) + // Completed Date
        (stageForColumns === "offers" ? 1 : 0) + // Offer Sent Date
        (stageForColumns === "closed" ? 1 : 0) + // Closed Date
        (showFlowColumns && stageForColumns === "pending_assessment" ? 2 : 0) + // Flow Progress + Current Step
        (showFlowColumns && stageForColumns === "to_review" ? 1 : 0); // Next Step

    return (
        <table className="w-full min-w-max">
            <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500">
                        <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={onSelectAllApplicants}
                            aria-label="Select all applicants"
                        />
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("candidate", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <div className="flex flex-row gap-2 justify-start items-center user-select-none cursor-pointer">
                            <span className="user-select-none">Candidate</span>
                            {onSortBy("candidate")}
                        </div>
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("location", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <div className="flex flex-row gap-2 justify-start items-center user-select-none cursor-pointer">
                            <span className="user-select-none">Location</span>
                            {onSortBy("location")}
                        </div>
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("created_at", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                            <span className="user-select-none">Applied Date</span>
                            {onSortBy("created_at")}
                        </div>
                    </th>
                    {stageForColumns === "pending_assessment" && (
                        <th
                            className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                            onClick={() => onSortDirection("pending_assessment_at", sortDirection === "asc" ? "desc" : "asc")}
                        >
                            <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                                <span className="user-select-none">Sent Date</span>
                                {onSortBy("pending_assessment_at")}
                            </div>
                        </th>
                    )}
                    {activeFunnelStage === "to_review" && (
                        <th
                            className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                            onClick={() => onSortDirection("completed_at", sortDirection === "asc" ? "desc" : "asc")}
                        >
                            <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                                <span className="user-select-none">Completed Date</span>
                                {onSortBy("completed_at")}
                            </div>
                        </th>
                    )}
                    {/* Hiring-flow columns (#3073/#3075) — custom-flow jobs only */}
                    {showFlowColumns && stageForColumns === "pending_assessment" && (
                        <>
                            <th className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                <span className="user-select-none">Flow Progress</span>
                            </th>
                            <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                <span className="user-select-none">Current Step</span>
                            </th>
                        </>
                    )}
                    {/* "Just Completed" column removed; "Next Step" moved to after Insights (below). */}
                    {activeFunnelStage === "offers" && (
                        <th
                            className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                            onClick={() => onSortDirection("offer_sent_at", sortDirection === "asc" ? "desc" : "asc")}
                        >
                            <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                                <span className="user-select-none">Offer Sent Date</span>
                                {onSortBy("offer_sent_at")}
                            </div>
                        </th>
                    )}
                    {activeFunnelStage === "closed" && (
                        <th
                            className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                            onClick={() => onSortDirection("closed_at", sortDirection === "asc" ? "desc" : "asc")}
                        >
                            <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                                <span className="user-select-none">Closed Date</span>
                                {onSortBy("closed_at")}
                            </div>
                        </th>
                    )}
                    <th
                        className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("status", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                            <span className="user-select-none">Status</span>
                            {onSortBy("status")}
                        </div>
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer"
                        onClick={() => onSortDirection("similarity_score", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <span className="user-select-none">Match</span>
                        {onSortBy("similarity_score")}
                    </th>
                    <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <span className="user-select-none">Insights</span>
                    </th>
                    {showFlowColumns && activeFunnelStage === "to_review" && (
                        <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <span className="user-select-none">Next Step</span>
                        </th>
                    )}
                    <th className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <span className="user-select-none">&nbsp;</span>
                    </th>
                    <th className="h-[45px] px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                        <span className="user-select-none">&nbsp;</span>
                    </th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {isLoading ? (
                    <tr>
                        <td colSpan={columnCount}>
                            <LoadingBox />
                        </td>
                    </tr>
                ) : (
                    <>
                        {applicants.length > 0 ? (
                            applicants.map((applicant, index) => (
                                <ApplicantItem
                                    key={index}
                                    job={job}
                                    activeTab={stageForColumns}
                                    applicant={applicant}
                                    shortlistedCount={shortlistedCountMap[applicant.applicant_id] ?? 0}
                                    commentCount={commentCountMap[applicant.id] ?? 0}
                                    applicantInsight={insightMap[applicant.applicant_id]}
                                    isLoadingInsight={loadingInsightIds.has(applicant.applicant_id)}
                                    onViewProfile={() => onViewProfile(applicant)}
                                    onViewReview={onViewReview ? () => onViewReview(applicant) : undefined}
                                    onShowRejectReason={() => onShowRejectReason(applicant)}
                                    onUpdateScoreSimilarity={onUpdateScoreSimilarity}
                                    onSelectedApplicant={onSelectedApplicant}
                                    onApplicantStatusChange={onApplicantStatusChange}
                                    onDeleteCandidate={onDeleteCandidate}
                                    showFlowColumns={showFlowColumns}
                                    onFlowAction={onFlowAction}
                                    onRejectFlagged={onRejectFlagged}
                                />
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columnCount} className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                    <EmptyState
                                        icon={<SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                                        title={getEmptyStateContent(activeFunnelStage).title}
                                        description={getEmptyStateContent(activeFunnelStage).description}
                                    />
                                </td>
                            </tr>
                        )}
                    </>
                )}
            </tbody>
        </table>
    );
}
